import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Settings, UserPlus, LogOut } from "lucide-react";
import { useUserInfo } from "../../context/user";
import { useUsersContext } from "../../context/usersContext";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "components/ui/popover";
import { Button } from "components/ui/button";
import { cn } from "lib/utils";
import { tabToHash } from "pages/Settings/tabSlugs";

/**
 * Configure popover pinned at the bottom of the app-shell left nav (Lovable
 * AppSidebar footer zone). Opens a grouped list of the Settings sections -
 * Workspace / Governance / Account - each navigating to /app/settings with a
 * `{ tab }` route-state, the same mechanism Settings/index.js already reads
 * (location.state?.tab). The Billing/Plans/Wallet items are gated identically
 * to Settings/index.js (Billing hidden for free locations; Plans only for
 * non-free locations whose active plan is account-enabled or a developer plan;
 * Wallet only when the active plan's rebilling.ai_active is true) — a menu item
 * whose tab doesn't mount would be a dead link.
 *
 * Below the trigger sit two always-visible nav-footer rows mirroring Lovable:
 * "Add team members" (jumps to the Team tab) and "Log out" (the context clear
 * fn, useUserInfo()[2]). Reads its own context and takes no props.
 */
const ROW_BASE =
  "h-9 w-full justify-start gap-2 px-3 text-sm font-normal text-muted-foreground border-0";

export default function ConfigureMenu({ collapsed = false }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Icon-only footer rows when the rail is collapsed (labels hidden; the
  // aria-labels on each button keep the accessible name).
  const rowClass = collapsed
    ? "h-9 w-9 mx-auto justify-center px-0 text-sm font-normal text-muted-foreground border-0"
    : ROW_BASE;

  const userInfo = useUserInfo();
  const logout = userInfo[2];
  const userActiveLocation = userInfo[4];

  const { products } = useUsersContext() || {};

  const gates = userActiveLocation?.plan_gates;
  const activePlan = products?.find((item) => item?.activePlan === true);

  let planGates;
  if (gates) {
    planGates = {
      plans: Boolean(gates.isenable_account || gates.developer_plan),
      wallet: gates.rebilling_ai_active === true,
    };
  } else if (activePlan) {
    planGates = {
      plans: Boolean(activePlan.isenable_account || activePlan.developer_plan),
      wallet: activePlan.rebilling?.ai_active === true,
    };
  } else {
    planGates = { plans: true, wallet: false };
  }

  const [open, setOpen] = useState(false);

  const onSettings = location.pathname.includes("/app/settings");

  const groups = [
    {
      label: "Workspace",
      items: [
        { label: "Tools", tab: "Integrations" },
        { label: "Webhooks", tab: "Webhooks" },
        { label: "Knowledge Base", tab: "Knowledge Base" },
        { label: "Data Interpreter", tab: "Data Interpreter" },
        { label: "API Access", tab: "API Access" },
      ],
    },
    {
      label: "Governance",
      items: [
        { label: "Guardrails", tab: "Guardrails" },
        { label: "Team", tab: "Team" },
      ],
    },
    {
      label: "Account",
      items: [
        ...(userActiveLocation?.type !== "free"
          ? [{ label: "Billing", tab: "Billing" }]
          : []),
        ...(userActiveLocation?.type !== "free" && planGates.plans
          ? [{ label: "Plans", tab: "Pricing" }]
          : []),
        { label: "Profile", tab: "Profile" },
        ...(planGates.wallet ? [{ label: "Wallet", tab: "Wallet" }] : []),
      ],
    },
  ];

  const goToTab = (tab) => {
    navigate(`/app/settings${tabToHash(tab)}`);
    setOpen(false);
  };

  return (
    <div className="flex flex-col gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            aria-label="Configure"
            data-active={onSettings || undefined}
            aria-current={onSettings ? "page" : undefined}
            title={collapsed ? "Configure" : undefined}
            className={cn(
              rowClass,
              onSettings
                ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground"
                : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            )}
          >
            <Settings size={16} aria-hidden="true" />
            {!collapsed && <span className="flex-1 text-left">Configure</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent side="top" align="start" className="w-60 p-2">
          <div className="flex flex-col gap-2">
            {groups.map((group, idx) => (
              <div
                key={group.label}
                className={cn(
                  "flex flex-col",
                  idx > 0 && "mt-1 border-t border-border pt-2"
                )}
              >
                <div className="px-2 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {group.label}
                </div>
                {group.items.map((item) => (
                  <Button
                    key={item.label}
                    type="button"
                    variant="ghost"
                    onClick={() => goToTab(item.tab)}
                    className={cn(
                      "h-8 w-full justify-start gap-2 px-2 text-sm font-normal text-foreground border-0",
                      "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    )}
                  >
                    <span className="flex-1 text-left">{item.label}</span>
                  </Button>
                ))}
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      <Button
        type="button"
        variant="ghost"
        aria-label="Add team members"
        title={collapsed ? "Add team members" : undefined}
        onClick={() => navigate(`/app/settings${tabToHash("Team")}`)}
        className={cn(
          rowClass,
          "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        )}
      >
        <UserPlus size={16} aria-hidden="true" />
        {!collapsed && <span className="flex-1 text-left">Add team members</span>}
      </Button>

      <Button
        type="button"
        variant="ghost"
        aria-label="Log out"
        title={collapsed ? "Log out" : undefined}
        onClick={() => logout && logout()}
        className={cn(
          rowClass,
          "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        )}
      >
        <LogOut size={16} aria-hidden="true" />
        {!collapsed && <span className="flex-1 text-left">Log out</span>}
      </Button>
    </div>
  );
}
